import React, { useState } from 'react';
import { Formik } from 'formik';
import { Button, Col, Collapse, Divider, Row, Spin, Typography } from 'antd';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { omit } from 'lodash/object';
import getTestPushNotificationData from '../../../../lib/getTestPushNotificationData';
import handleResponse from '../../../../lib/handleResponse';
import { Permissions } from '../../../../const/permissions';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import Spinner from '../../../../components/Spinner';
import TitleWithAutomaticGeneration from '../../../../components/TitleWithAutomaticGeneration';
import mkTemplateDynamicFormData from '../../../Content/utils/mkTemplateDynamicFormData';
import DynamicFormDataFields from '../../../Content/components/DynamicFormDataFields';
import TemplateVisualization from '../../../Template builder/components/TemplateVisualization';
import { useGetTemplatesVisualizationsQuery } from '../../../Template builder/api/templatesVisualizationsApiSlice';
import { useSendTestPushNotificationMutation } from '../../api/pushNotificationsApiSlice';



const { Title } = Typography;
const { Panel } = Collapse;

const FormEditPushNotification = ({
  initialErrors,
  initialValues,
  templateFields,
  templateOptions,
  validationSchema,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(templateFields);
  const language = useSelector(({ customise: { language } }) => language);
  const [ sendTestButtonDisabled, setSendTestButtonDisabled ] = useState(false);


  const {
    data: { data: visualizations } = { data: [] },
    isFetching: isLoadingVisualizations,
  } = useGetTemplatesVisualizationsQuery({
    queryParams: `search=template_id:${initialValues?.template_id}`,
  }, { skip: !initialValues?.template_id });

  const [ sendTestPushNotification, { isLoading: isSendingTestPushNotification } ] = useSendTestPushNotificationMutation();

  const sendTestPush = (values) => {
    const formData = getTestPushNotificationData(values);

    sendTestPushNotification({ formData })
      .unwrap()
      .then(() => handleResponse('success', intl, 'push-notifications-send-test-success', [
        () => setSendTestButtonDisabled(true),
        () => setTimeout(() => setSendTestButtonDisabled(false), 10000) ]))
      .catch((error) => handleResponse('error', intl, 'push-notifications-send-test-error', [], error));
  };

  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }
  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, templateFields), resetForm);
      }}
      onReset={async (values) => {
        onCancel(values);
      }}
    >
      {({ isValid, values, setFieldValue, resetForm }) => {
        const isDirty = !isEqual(
          omit(values, [ 'visualization', 'language', 'user_token' ]),
          omit(initialValues, [ 'visualization', 'language', 'user_token' ]),
        );

        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={false}
                          allowClear
                          disabled
                          name="template_id"
                          options={templateOptions}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <Collapse accordion className='hp-mb-24'>
                        <Panel
                          key="additional-info"
                          header={
                            <Title className='collapse-item-title' level={5}>
                              {intl.formatMessage({ id: 'ui-general-optional-data' })}
                            </Title>
                          }
                        >
                          <TitleWithAutomaticGeneration useAutomaticGeneration={false} />
                        </Panel>
                      </Collapse>
                    </Col>

                    <Col span={24}>
                      <Divider />
                      <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-template-data" /></Title>
                      {templateFields.length ? (
                        <Row>
                          <Col span={24}>
                            <DynamicFormDataFields
                              setFieldValue={setFieldValue}
                              fields={mainFields}
                              values={values}
                              span={24}
                            />
                            {!!optionalFields.length && (
                              <Collapse>
                                <Panel
                                  key="1"
                                  header={
                                    <Title level={5} className='collapse-item-title'>
                                      <IntlMessages id="ui-general-additional-parameters" />
                                    </Title>
                                  }
                                >
                                  <DynamicFormDataFields
                                    setFieldValue={setFieldValue}
                                    fields={optionalFields}
                                    values={values}
                                    span={24}
                                  />
                                </Panel>
                              </Collapse>
                            )}
                          </Col>
                        </Row>
                      ) : <Row gutter={16} className="hp-d-flex-justify-center"><Spin spinning /></Row>}
                    </Col>
                  </Row>
                </Col>

                <Col md={12} xs={24}>
                  <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.VIEW_NEWS_VISUALIZATIONS ]}>
                    <TemplateVisualization
                      isLoading={isLoadingVisualizations}
                      currentValues={values}
                      visualizations={visualizations}
                      dynamicData={templateFields}
                    />
                  </PrivateComponent>
                  <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.TEST ]}>
                    <div className='send-test-push-notification-block hp-mb-24'>
                      <FormItem
                        name='user_token'
                        required
                        label={<IntlMessages id="push-notifications-device-token" />}
                      >
                        <Input
                          name="user_token"
                          placeholder={intl.formatMessage({ id: 'push-notifications-device-token' })}
                        />
                      </FormItem>

                      <Button
                        className='w-full'
                        type="primary"
                        onClick={() => sendTestPush(values)}
                        loading={isSendingTestPushNotification || sendTestButtonDisabled}
                        disabled={!values.user_token || !templateFields.length || sendTestButtonDisabled}
                      >
                        <IntlMessages id="push-notifications-send-test" />
                      </Button>

                      <div className='disabled-hint-text hp-mt-8'>
                        <IntlMessages id="push-notifications-test-publish-note" />
                      </div>
                    </div>
                  </PrivateComponent>
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !isDirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormEditPushNotification.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  templateOptions: PropTypes.array.isRequired,
  templateFields: PropTypes.array.isRequired,
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormEditPushNotification;
